import { ParsedUrlQuery } from 'querystring';
import { useMemo } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import retry from 'async-retry';

import { Company, CompanyBanner } from 'components/company-page';
import { Page } from 'components/layouts';
import { getLayout } from 'components/layouts/Header';
import { PER_PAGE_COUNT } from 'hooks/useCompany/useCompanyInternal';
import {
  CompaniesResponse,
  CompanyResponse,
  TCompany,
} from 'lib/models/company';
import { CompanyProvider } from 'lib/providers/CompanyProvider';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { pageBuilderInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import { asyncRetryParams } from 'lib/consts';
import { OrganizationEventsProvider } from 'lib/providers/OrganizationEventsProvider';
import { OpportunitiesResponse, TOpportunity } from 'lib/models/opportunity';
import { useAuth } from 'lib/providers/AuthProvider';
import { getCompanyBreadcrumbSchema } from 'utils/seo';
import { getCompanyMetaDescription } from 'utils/company';

import styles from './CompanyPage.module.scss';

interface CompanyProps {
  company: TCompany;
  canonicalURL?: string;
  publishedJobs: Array<TOpportunity>;
  publishedJobsTotalCount: number;
  publishedInternshipsTotalCount: number;
  initialPublishedEventsResponse: OpportunitiesResponse;
  pastEventsInitialCount: number;
}

const CompanyPage: NextPage<CompanyProps> = ({
  company,
  canonicalURL,
  publishedJobs,
  publishedJobsTotalCount,
  publishedInternshipsTotalCount,
  initialPublishedEventsResponse,
  pastEventsInitialCount,
}) => {
  const { name: companyName, meta_tags } = company;
  const { isGuestCandidate } = useAuth();
  const pageTitle = `${companyName} | Huzzle`;
  const metaDescription = getCompanyMetaDescription(company);
  const keywords = useMemo(() => {
    const tags = parseArrayResponse(meta_tags);
    if (tags.length === 0) return null;
    return tags.map((tag) => tag.value).join(', ');
  }, [meta_tags]);

  const showBanner = isGuestCandidate && publishedJobsTotalCount > 0;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {canonicalURL && <link rel="canonical" href={canonicalURL} />}
        {keywords && <meta name="keywords" content={keywords} />}
        <script
          id="companyBreadcrumbSchema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getCompanyBreadcrumbSchema({ company }),
          }}
        />
      </Head>
      {showBanner && (
        <CompanyBanner
          companyName={companyName}
          jobsCount={publishedJobsTotalCount}
        />
      )}
      <Page className={styles.page} hasBannerAbove={showBanner}>
        <CompanyProvider
          company={company}
          publishedJobs={publishedJobs}
          publishedJobsTotalCount={publishedJobsTotalCount}
          publishedInternshipsTotalCount={publishedInternshipsTotalCount}
        >
          <OrganizationEventsProvider
            organization={company}
            initialPublishedEventsResponse={initialPublishedEventsResponse}
            pastEventsInitialCount={pastEventsInitialCount}
            perPageCount={5}
          >
            <Company className={styles.company} />
          </OrganizationEventsProvider>
        </CompanyProvider>
      </Page>
    </>
  );
};

// @ts-ignore
CompanyPage.getLayout = getLayout;

export default CompanyPage;

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const { data } = await pageBuilderInstance<CompaniesResponse>(
    `${API_ROUTES.COMPANY_LIST}/basic_index?per_page=100000`
  );
  const companies = parseArrayResponse(data);
  const paths = companies.map(({ slug }) => {
    return {
      params: { slug },
    };
  });
  return { paths, fallback: 'blocking' };
};

interface Params extends ParsedUrlQuery {
  slug: string;
}

export const getStaticProps: GetStaticProps<CompanyProps, Params> = async (
  context
) => {
  let slug = '';
  let propsData;

  try {
    slug = context.params?.slug as string;
    const pathname = `${API_ROUTES.COMPANY_LIST}/${slug}`;
    const publishedJobsRoute = `${API_ROUTES.COMPANY_LIST}/${slug}/touchpoints?touchpointable_types[]=Job&touchpointable_types[]=Internship&per_page=5&sort_by=posted_at&sort_order=desc&with_details=true`;
    const publishedInternshipsRoute = `${API_ROUTES.COMPANY_LIST}/${slug}/touchpoints?touchpointable_types[]=Internship&per_page=1`;
    const featuredPublishedJobsRoute = `${publishedJobsRoute}&featured=true`;
    const nonFeaturedPublishedJobsRoute = `${publishedJobsRoute}&featured=false`;
    const companyPublishedEventsPathname = `${API_ROUTES.COMPANY_LIST}/${slug}/touchpoints?touchpointable_type=Event&with_details=true&page=1&per_page=${PER_PAGE_COUNT}`;
    const companyPastEventsPathname = `${API_ROUTES.COMPANY_LIST}/${slug}/touchpoints?touchpointable_type=Event&past=true&per_page=0`;

    propsData = await retry(async () => {
      const companyPromise = pageBuilderInstance<CompanyResponse>(pathname);
      const nonFeaturedPublishedJobsPromise =
        pageBuilderInstance<OpportunitiesResponse>(
          nonFeaturedPublishedJobsRoute
        );
      const featuredPublishedJobsPromise =
        pageBuilderInstance<OpportunitiesResponse>(featuredPublishedJobsRoute);
      const publishedEventsPromise = pageBuilderInstance<OpportunitiesResponse>(
        companyPublishedEventsPathname
      );
      const pastEventsPromise = pageBuilderInstance<OpportunitiesResponse>(
        companyPastEventsPathname
      );
      const publishedInternshipsPromise =
        pageBuilderInstance<OpportunitiesResponse>(publishedInternshipsRoute);

      const [
        { data: companyResponse },
        { data: featuredPublishedJobsResponse },
        { data: nonFeaturedPublishedJobsResponse },
        { data: publishedEventsResponse },
        { data: pastEventsResponse },
        { data: publishedInternshipsResponse },
      ] = await Promise.all([
        companyPromise,
        featuredPublishedJobsPromise,
        nonFeaturedPublishedJobsPromise,
        publishedEventsPromise,
        pastEventsPromise,
        publishedInternshipsPromise,
      ]);

      const company = parseResponse(companyResponse);
      const publishedFeaturedJobs = parseArrayResponse(
        featuredPublishedJobsResponse
      );
      const publishedNonFeaturedJobs = parseArrayResponse(
        nonFeaturedPublishedJobsResponse
      );
      const publishedJobs = [
        ...publishedFeaturedJobs,
        ...publishedNonFeaturedJobs,
      ].splice(0, 5);
      const publishedJobsTotalCount =
        featuredPublishedJobsResponse.meta.total +
        nonFeaturedPublishedJobsResponse.meta.total;
      const initialPublishedEventsResponse = publishedEventsResponse;
      const pastEventsInitialCount = pastEventsResponse.meta.total || 0;
      const publishedInternshipsTotalCount =
        publishedInternshipsResponse.meta.total || 0;
      const canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/companies/${slug}`;
      return {
        company,
        canonicalURL,
        publishedJobs,
        publishedJobsTotalCount,
        publishedInternshipsTotalCount,
        initialPublishedEventsResponse,
        pastEventsInitialCount,
      };
    }, asyncRetryParams);
  } catch (error) {
    console.error(`Company page not found - ${slug}: ${error}`);
    return { notFound: true };
  }

  return {
    props: {
      ...propsData,
      ...(await fetchTranslationFiles({
        locale: context.locale,
        pages: [LOCALE_PAGES.COMPANY],
      })),
    },
    revalidate: 3600,
  };
};
